// src/features/deadlines/components/ReleaseModal.tsx
//
// Modal-Komponente für die Finalisierung eines DATEV-Kunden mit
// Versandart 'xtax_portal' (Stufe 3.1).
//
// Schwester-Komponente von UploadModal:
//   - kein PDF-Upload-Feld (DATEV liefert die Auswertungen)
//   - Empfänger-Auswahl aus customer_contacts mit
//     receives_finalization_package = true UND is_active = true
//   - Auto-Select bei genau einem Empfänger, Dropdown bei mehreren
//   - Hard-Stop (Button ausgegraut + Hinweis) wenn 0 Empfänger verfügbar
//   - kein Test-Modus (anders als UploadModal): der Versand passiert
//     vom Edge-Handler später, nicht direkt beim Klick. Test-Vorschau
//     wird ggf. in einer späteren Stufe als separates Feature ergänzt.
//
// Zugangs-Gate (entschärft): Finalisieren ist erlaubt, wenn der Empfänger
// ein Passwort gesetzt hat ODER eine noch gültige Passwort-Einladung offen
// ist (hasPasswordSetupPending). Nur wenn beides fehlt, wird gebremst —
// dann wurde dem Empfänger noch kein Zugangsweg eröffnet.
//
// DATEV-Token-Gate: Finalisieren ist blockiert, wenn für Berater/Mandant
// kein gültiger DATEV-Token vorliegt. Ohne Token spiegelt der Sync keine
// Auswertungen, das Paket würde nie gebaut (stiller Fehlschlag). Der Check
// läuft über die RPC check_datev_token_status (SECURITY DEFINER, gibt nur
// Status-Booleans zurück, nie das Token selbst). Der permission_probe_status
// wird informativ angezeigt, blockt aber nicht hart.
//
// Versandzeitpunkt (24.09.2026): DATEV stellt geänderte Lohndaten nur zu festen
// Zeitpunkten in die Schnittstelle. Standard ist deshalb der Versand nach der
// nächsten DATEV-Bereitstellung. Sofortversand ist nur mit der Bestätigung
// möglich, dass alle Änderungen und die Übertragung an den
// Lohnaustauschdatenservice vor der letzten Bereitstellung abgeschlossen waren.
// Nächste und letzte Bereitstellung liefert die RPC datev_bereitstellung_info,
// dieselbe Quelle, die auch die Versandlogik im Backend nutzt.
//
// Beim "Bereitstellen"-Klick:
//   onRelease(contactId, additionalFiles, senderIdentity, customMessage, confirmedTodos, versandSteuerung)
// wird im Hook (useDeadlines.handleRelease) verarbeitet:
//   1. Manuelle Anhänge in den Staging-Bereich hochladen
//   2. Pending-Task xtax_portal_release anlegen mit den Staging-Pfaden
//      und dem gewählten Versandmodus
//   3. Modal schließen und Status aktualisieren

import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Truck, ShieldAlert, Lock, Users, Clock } from 'lucide-react';
import { Customer, CustomerCustomTodo, CustomerCustomAttachment, CustomerContact } from '../../../shared/types';
import { EmailSenderIdentity } from '../../../services/email/types';
import { TodoSection } from './TodoSection';
import { AttachmentSection } from './AttachmentSection';
import { SenderSelection } from './SenderSelection';
import { CustomMessageInput } from './CustomMessageInput';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSenderManagement } from '../hooks/useSenderManagement';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { supabase } from '../../../shared/services/supabase';

// Versandmodus des Monatsabschlusspakets. Wird unverändert als
// context.versand_modus am Pending-Task gespeichert und von der Engine
// (supabase/functions/_shared/datev/pending-tasks.ts) ausgewertet.
export type ReleaseVersandModus = 'nach_bereitstellung' | 'sofort';

export interface ReleaseVersandSteuerung {
  modus: ReleaseVersandModus;
  // Nur bei Sofortversand gesetzt: die letzte DATEV-Bereitstellung (ISO), auf
  // die sich die Bestätigung des Mitarbeiters bezieht.
  letzteBereitstellung: string | null;
}

interface BereitstellungInfo {
  naechsteBereitstellung: string;
  naechsterAbruf: string;
  letzteBereitstellung: string | null;
}

// Puffer zwischen maßgeblichem Abruf und voraussichtlichem Versand in der
// Anzeige. Der Paketbau läuft über die minütliche Prüfung nach dem Abruf.
const ANZEIGE_VERSAND_PUFFER_MINUTEN = 5;

// "Freitag, 25.09., 10:30 Uhr" in Berliner Zeit.
const formatBereitstellung = (iso: string): string => {
  const d = new Date(iso);
  const datum = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: 'long',
    day: '2-digit',
    month: '2-digit'
  }).format(d);
  const uhrzeit = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
  return `${datum}, ${uhrzeit} Uhr`;
};

// "10:55" in Berliner Zeit, optional um Minuten verschoben.
const formatUhrzeit = (iso: string, plusMinuten = 0): string => {
  const d = new Date(new Date(iso).getTime() + plusMinuten * 60 * 1000);
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
};

interface ReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRelease: (
    contactId: string,
    additionalFiles?: Record<string, File>,
    senderIdentity?: EmailSenderIdentity,
    customMessage?: string,
    confirmedTodos?: string[],
    versandSteuerung?: ReleaseVersandSteuerung
  ) => Promise<void>;
  customer: Customer;
  month: number;
  year: number;
  todos?: CustomerCustomTodo[];
  attachments?: CustomerCustomAttachment[];
}

export const ReleaseModal: React.FC<ReleaseModalProps> = ({
  isOpen,
  onClose,
  onRelease,
  customer,
  month,
  year,
  todos,
  attachments
}) => {
  // Empfänger-State
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // Restlicher State (analog UploadModal)
  const [isReleasing, setIsReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedAttachments, setUploadedAttachments] = useState<Record<string, File>>({});
  const [customMessage, setCustomMessage] = useState('');
  const [confirmedTodos, setConfirmedTodos] = useState<string[]>([]);
  const [filteredTodos, setFilteredTodos] = useState<CustomerCustomTodo[]>([]);

  // Gate-State (Gate-Erweiterung):
  // Passwort-Gate entschärft — offene gültige Einladung zählt als Zugangsweg.
  // DATEV-Token-Gate — blockt bei fehlendem gültigen Token für Berater/Mandant.
  const [hasSetupPending, setHasSetupPending] = useState(false);
  const [accessCheckLoading, setAccessCheckLoading] = useState(false);
  const [datevTokenOk, setDatevTokenOk] = useState(false);
  const [datevTokenLoading, setDatevTokenLoading] = useState(false);
  const [datevProbeStatus, setDatevProbeStatus] = useState<string | null>(null);

  // Versandzeitpunkt: Standard ist der Versand nach der nächsten
  // DATEV-Bereitstellung. Sofortversand nur mit ausdrücklicher Bestätigung.
  const [versandModus, setVersandModus] = useState<ReleaseVersandModus>('nach_bereitstellung');
  const [sofortBestaetigt, setSofortBestaetigt] = useState(false);
  const [bereitstellungInfo, setBereitstellungInfo] = useState<BereitstellungInfo | null>(null);
  const [bereitstellungLoading, setBereitstellungLoading] = useState(false);
  const [bereitstellungFehler, setBereitstellungFehler] = useState<string | null>(null);

  const { user } = useAuth();

  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError,
    photoValidationErrors,
    senderIdentityUnconfirmed
  } = useSenderManagement(isOpen, user?.email);

  // To-Dos nach aktuellem Monat filtern (identisch zu UploadModal)
  useEffect(() => {
    if (todos) {
      const filtered = todos.filter(todo => {
        if (!todo.active_months || todo.active_months.length === 0) return true;
        return todo.active_months.includes(month);
      });
      setFilteredTodos(filtered);
    }
  }, [todos, month]);

  // Empfänger laden beim Öffnen
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadContacts = async () => {
      setContactsLoading(true);
      setSelectedContactId(null);
      setError(null);

      const finalizationContacts = await customerContactService.getFinalizationContacts(customer.id);

      if (cancelled) return;

      setContacts(finalizationContacts);

      // Auto-Select bei genau einem verfügbaren Empfänger
      if (finalizationContacts.length === 1) {
        setSelectedContactId(finalizationContacts[0].id);
      }

      setContactsLoading(false);
    };

    loadContacts();

    return () => {
      cancelled = true;
    };
  }, [isOpen, customer.id]);

  // Zugangs-Prüfung pro gewähltem Empfänger: offene gültige Passwort-Einladung?
  // Wird zusätzlich zum synchron vorliegenden Passwort-Status ausgewertet.
  useEffect(() => {
    if (!selectedContactId) {
      setHasSetupPending(false);
      setAccessCheckLoading(false);
      return;
    }

    let cancelled = false;
    setAccessCheckLoading(true);

    customerContactService.hasPasswordSetupPending(selectedContactId)
      .then(pending => {
        if (cancelled) return;
        setHasSetupPending(pending);
        setAccessCheckLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHasSetupPending(false);
        setAccessCheckLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedContactId]);

  // DATEV-Token-Gate: prüft über die RPC, ob für Berater/Mandant ein gültiger
  // Token vorliegt. Läuft auf Kunden-Ebene beim Öffnen. Ohne DATEV-Anbindung
  // ist das Gate nicht anwendbar (der bestehende Berater-/Mandant-Guard greift).
  useEffect(() => {
    if (!isOpen) return;

    const ber = customer.beraternummer;
    const man = customer.mandantennummer;

    if (!ber || !man) {
      setDatevTokenOk(false);
      setDatevProbeStatus(null);
      setDatevTokenLoading(false);
      return;
    }

    let cancelled = false;
    setDatevTokenLoading(true);

    supabase
      .rpc('check_datev_token_status', {
        p_beraternummer: ber,
        p_mandantennummer: man
      })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          console.error('[ReleaseModal] check_datev_token_status fehlgeschlagen:', rpcError);
          setDatevTokenOk(false);
          setDatevProbeStatus(null);
        } else {
          const row = Array.isArray(data) ? data[0] : data;
          setDatevTokenOk(row?.has_valid_token === true);
          setDatevProbeStatus(row?.probe_status ?? null);
        }
        setDatevTokenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, customer.beraternummer, customer.mandantennummer]);

  // DATEV-Bereitstellungszeitpunkte laden beim Öffnen. Ohne diese Angaben ist
  // keine bewusste Entscheidung über den Versandzeitpunkt möglich, deshalb
  // bleibt die Bereitstellung dann gesperrt.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setVersandModus('nach_bereitstellung');
    setSofortBestaetigt(false);
    setBereitstellungInfo(null);
    setBereitstellungFehler(null);
    setBereitstellungLoading(true);

    supabase
      .rpc('datev_bereitstellung_info')
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (rpcError || !row?.naechste_bereitstellung || !row?.naechster_abruf) {
          if (rpcError) {
            console.error('[ReleaseModal] datev_bereitstellung_info fehlgeschlagen:', rpcError);
          }
          setBereitstellungInfo(null);
          setBereitstellungFehler('Die DATEV-Bereitstellungszeiten konnten nicht geladen werden. Bitte lade die Seite neu und versuche es noch einmal.');
        } else {
          setBereitstellungInfo({
            naechsteBereitstellung: row.naechste_bereitstellung,
            naechsterAbruf: row.naechster_abruf,
            letzteBereitstellung: row.letzte_bereitstellung ?? null
          });
        }
        setBereitstellungLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getMonthName = (monthNumber: number): string => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return months[monthNumber - 1] || '';
  };

  // Abgeleitete Werte vom gewählten Empfänger
  const selectedContact = contacts.find(c => c.id === selectedContactId) || null;
  const hasPassword = selectedContact
    ? !!selectedContact.secure_download_passwort && selectedContact.secure_download_passwort.trim() !== ''
    : false;
  const hasToken = selectedContact
    ? !!selectedContact.secure_download_token && selectedContact.secure_download_token.trim() !== ''
    : false;

  // Zugangsweg vorhanden: Passwort gesetzt ODER gültige Einladung offen.
  const canAccess = hasPassword || hasSetupPending;

  // DATEV-Token-Gate nur anwendbar, wenn Berater-/Mandantennummer gesetzt sind.
  const datevGateApplicable = !!customer.beraternummer && !!customer.mandantennummer;

  // Sofortversand setzt eine bekannte letzte Bereitstellung voraus, auf die
  // sich die Bestätigung bezieht.
  const sofortMoeglich = !!bereitstellungInfo?.letzteBereitstellung;

  // Handler — Logik identisch zu UploadModal
  const handleTodoConfirmation = (todoId: string, confirmed: boolean) => {
    if (confirmed) {
      setConfirmedTodos(prev => [...prev.filter(id => id !== todoId), todoId]);
    } else {
      setConfirmedTodos(prev => prev.filter(id => id !== todoId));
    }
    setError(null);
  };

  const handleAttachmentSelect = (attachmentId: string, file: File) => {
    setUploadedAttachments(prev => ({ ...prev, [attachmentId]: file }));
    setError(null);
  };

  const handleAttachmentRemove = (attachmentId: string) => {
    setUploadedAttachments(prev => {
      const newFiles = { ...prev };
      delete newFiles[attachmentId];
      return newFiles;
    });
  };

  const handleSenderChange = (sender: EmailSenderIdentity) => {
    setSelectedSender(sender);
  };

  const handleVersandModusChange = (modus: ReleaseVersandModus) => {
    setVersandModus(modus);
    // Die Bestätigung gilt nur für die bewusste Wahl des Sofortversands und
    // wird bei jedem Wechsel zurückgesetzt.
    setSofortBestaetigt(false);
    setError(null);
  };

  // Erzwungener Relogin: entfernt das App-Token und lädt die Seite neu.
  // useAuth rekonstruiert danach keinen User mehr → App.tsx zeigt die
  // LoginPage. Nutzt bewusst den vorhandenen Token-Mechanismus, ohne die
  // Auth-Architektur anzufassen.
  const handleForceRelogin = () => {
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  // Validierung — identisch zu UploadModal-Logik (außer file-Check)
  const requiredAttachments = (attachments || []).filter(att => att.is_required);
  const allRequiredAttachmentsUploaded = requiredAttachments.length > 0
    ? requiredAttachments.every(att => uploadedAttachments[att.id])
    : true;

  const todosRequiringConfirmation = filteredTodos.filter(todo => todo.requires_confirmation);
  const allRequiredTodosConfirmed = todosRequiringConfirmation.every(
    todo => confirmedTodos.includes(todo.id)
  );
  const hasUnconfirmedTodos = todosRequiringConfirmation.length > 0 && !allRequiredTodosConfirmed;

  const handleRelease = async () => {
    if (contacts.length === 0) {
      setError('Es ist kein Empfänger mit Finalisierungs-Berechtigung hinterlegt. Bitte legen Sie zuerst einen Ansprechpartner mit aktiviertem "Erhält Finalisierungs-Paket" an.');
      return;
    }

    if (!selectedContactId || !selectedContact) {
      setError('Bitte wählen Sie einen Empfänger aus.');
      return;
    }

    if (!canAccess) {
      setError('Der gewählte Empfänger hat weder ein Passwort gesetzt noch eine gültige Passwort-Einladung offen. Bitte senden Sie zuerst eine Passwort-Einladung in den Stammdaten des Ansprechpartners.');
      return;
    }

    if (!hasToken) {
      setError('Der gewählte Empfänger hat keinen Zugangs-Token. Bitte sprechen Sie den Administrator an — der Token sollte beim Anlegen automatisch erzeugt werden.');
      return;
    }

    if (hasUnconfirmedTodos) {
      const unconfirmedCount = todosRequiringConfirmation.filter(
        todo => !confirmedTodos.includes(todo.id)
      ).length;
      setError(`Bitte bestätigen Sie alle erforderlichen To-Dos (${unconfirmedCount} von ${todosRequiringConfirmation.length} noch offen)`);
      return;
    }

    if (!allRequiredAttachmentsUploaded) {
      setError(`Bitte laden Sie alle erforderlichen Anhänge hoch (${Object.keys(uploadedAttachments).length}/${requiredAttachments.length})`);
      return;
    }

    if (!customer.beraternummer || !customer.mandantennummer) {
      setError('Kunde hat keine vollständige DATEV-Anbindung (Berater-/Mandantennummer fehlen).');
      return;
    }

    if (datevGateApplicable && !datevTokenOk) {
      setError('Für diesen Mandanten liegt kein gültiger DATEV-Token vor. Solange kein Token freigegeben ist, werden die Auswertungen nicht aus DATEV gespiegelt und das Paket kann nicht erstellt werden.');
      return;
    }

    if (!bereitstellungInfo) {
      setError(bereitstellungFehler || 'Die DATEV-Bereitstellungszeiten sind noch nicht geladen.');
      return;
    }

    if (versandModus === 'sofort') {
      if (!sofortMoeglich) {
        setError('Die letzte DATEV-Bereitstellung ist nicht bekannt. Sofortversand ist deshalb nicht möglich, bitte wähle den Versand nach der nächsten Bereitstellung.');
        return;
      }
      if (!sofortBestaetigt) {
        setError('Bitte bestätige, dass alle Änderungen und die Übertragung an den Lohnaustauschdatenservice vor der letzten DATEV-Bereitstellung abgeschlossen waren.');
        return;
      }
    }

    const versandSteuerung: ReleaseVersandSteuerung = {
      modus: versandModus,
      letzteBereitstellung: versandModus === 'sofort' ? bereitstellungInfo.letzteBereitstellung : null
    };

    setIsReleasing(true);
    setError(null);

    try {
      await onRelease(
        selectedContactId,
        uploadedAttachments,
        selectedSender,
        customMessage,
        confirmedTodos,
        versandSteuerung
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bereitstellung fehlgeschlagen');
      setIsReleasing(false);
    }
  };

  // Button-Disabled-Status
  const isButtonDisabled =
    isReleasing ||
    contactsLoading ||
    contacts.length === 0 ||
    !selectedContactId ||
    accessCheckLoading ||
    !canAccess ||
    !hasToken ||
    (datevGateApplicable && (datevTokenLoading || !datevTokenOk)) ||
    // Hard-Block: Solange der Absender nicht bewusst bestätigt ist (Sitzung
    // evtl. abgelaufen, still auf den Primär-Mitarbeiter zurueckgefallen), ist
    // die Bereitstellung gesperrt — verhindert unbemerktes Senden im falschen
    // Namen. Die Sender-Auswahl wird im xtax_portal-Flow immer angezeigt.
    senderIdentityUnconfirmed ||
    // Versandzeitpunkt: ohne geladene Bereitstellungszeiten keine Freigabe,
    // Sofortversand nur mit Bestätigung.
    bereitstellungLoading ||
    !bereitstellungInfo ||
    (versandModus === 'sofort' && (!sofortMoeglich || !sofortBestaetigt));

  const showSenderSelection = true;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>
            {customer.company} - {getMonthName(month)} {year}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
            disabled={isReleasing}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Customer Info */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
            {customer.beraternummer ? `Berater: ${customer.beraternummer} | ` : ''}
            {customer.mandantennummer ? `Mandant: ${customer.mandantennummer}` : 'Keine Mandantennummer'}
          </p>
        </div>

        {/* xtax_portal Info-Box */}
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#EFF6FF',
          borderRadius: '8px',
          border: '1px solid #BFDBFE'
        }}>
          <p style={{
            fontSize: '13px',
            color: '#1D4ED8',
            margin: 0,
            lineHeight: '1.5'
          }}>
            <strong>XTAX-Portal-Versand:</strong> Sobald die Auswertungen aus DATEV vorliegen, werden sie automatisch im Mandantenportal bereitgestellt und der gewählte Empfänger benachrichtigt. Es ist keine PDF-Auswahl nötig.
          </p>
        </div>

        {/* DATEV-Token-Gate: Warnung bei fehlendem gültigen Token */}
        {datevGateApplicable && !datevTokenLoading && !datevTokenOk && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <ShieldAlert size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#991B1B',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Kein gültiger DATEV-Token
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B91C1C',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Für Berater {customer.beraternummer} / Mandant {customer.mandantennummer} liegt aktuell kein gültiger DATEV-Token vor. Solange kein Token freigegeben ist, werden die Auswertungen nicht aus DATEV gespiegelt und das Paket kann nicht erstellt werden.
                {datevProbeStatus ? ` (DATEV-Prüfstatus: ${datevProbeStatus})` : ''}
              </p>
            </div>
          </div>
        )}

        {/* WARNUNG bei unbestätigten To-Dos */}
        {hasUnconfirmedTodos && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF3C7',
            borderRadius: '8px',
            border: '1px solid #F59E0B',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <ShieldAlert size={20} color="#F59E0B" />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#92400E',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Bestätigung erforderlich
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B45309',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Es gibt {todosRequiringConfirmation.length} To-Do{todosRequiringConfirmation.length > 1 ? 's' : ''},
                die vor der Bereitstellung bestätigt werden {todosRequiringConfirmation.length > 1 ? 'müssen' : 'muss'}.
              </p>
            </div>
          </div>
        )}

        {/* Postalischer Versand Hinweis */}
        {customer.postalischer_versand && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#EEF2FF',
            borderRadius: '8px',
            border: '1px solid #C7D2FE',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Truck size={20} color="#4338CA" />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#4338CA',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Achtung: <strong>Postalischer Versand</strong>
              </p>
              <p style={{
                fontSize: '13px',
                color: '#6366F1',
                margin: 0,
                lineHeight: '1.4'
              }}>
                DATEV versendet die Auswertungen zusätzlich per Postbrief an den Kunden.
              </p>
            </div>
          </div>
        )}

        {/* Empfänger-Auswahl */}
        {contactsLoading ? (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
              Empfänger werden geladen...
            </p>
          </div>
        ) : contacts.length === 0 ? (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <Users size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{
                fontSize: '14px',
                color: '#991B1B',
                margin: '0 0 4px 0',
                fontWeight: '600'
              }}>
                Kein Empfänger verfügbar
              </p>
              <p style={{
                fontSize: '13px',
                color: '#B91C1C',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Bitte legen Sie zuerst einen Ansprechpartner mit aktiviertem "Erhält Finalisierungs-Paket" in den Kundenstammdaten an.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Empfänger
            </label>

            {contacts.length === 1 ? (
              <div style={{
                padding: '10px 12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                border: '1px solid #D1D5DB'
              }}>
                <p style={{ fontSize: '14px', color: '#111827', margin: 0, fontWeight: '500' }}>
                  {customerContactService.getContactDisplayName(contacts[0])}
                  {contacts[0].rolle && (
                    <span style={{ fontWeight: 'normal', color: '#6B7280' }}> ({contacts[0].rolle})</span>
                  )}
                </p>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '2px 0 0 0' }}>
                  {contacts[0].email}{contacts[0].email_cc ? `, CC: ${contacts[0].email_cc}` : ''}
                </p>
              </div>
            ) : (
              <select
                value={selectedContactId || ''}
                onChange={(e) => {
                  setSelectedContactId(e.target.value || null);
                  setError(null);
                }}
                disabled={isReleasing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: selectedContactId ? '#374151' : '#9CA3AF',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="">Bitte Empfänger wählen...</option>
                {contacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {customerContactService.getContactDisplayName(contact)}
                    {contact.rolle ? ` (${contact.rolle})` : ''} – {contact.email}
                  </option>
                ))}
              </select>
            )}

            {/* Hinweis falls gewählter Empfänger keinen Zugangsweg hat
                (weder Passwort gesetzt noch gültige Einladung offen) */}
            {selectedContactId && !accessCheckLoading && !canAccess && (
              <div style={{
                marginTop: '8px',
                padding: '10px 12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '6px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <Lock size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: '1.4' }}>
                  Dieser Empfänger hat weder ein Passwort gesetzt noch eine gültige Passwort-Einladung offen. Bitte senden Sie zuerst eine Passwort-Einladung in den Stammdaten des Ansprechpartners.
                </p>
              </div>
            )}

            {/* Hinweis falls Token fehlt (defense-in-depth: sollte durch DB-Trigger nie passieren) */}
            {selectedContactId && canAccess && !hasToken && (
              <div style={{
                marginTop: '8px',
                padding: '10px 12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '6px',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <Lock size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: '1.4' }}>
                  Diesem Empfänger fehlt der Zugangs-Token. Bitte sprechen Sie den Administrator an — der Token sollte beim Anlegen automatisch erzeugt werden.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Sender Selection */}
        {showSenderSelection && (
          <>
            {/* Hard-Block: Absender konnte nicht ueber die eingeloggte
                User-E-Mail bestätigt werden (z. B. abgelaufene Sitzung) und
                wurde per Fallback vorbelegt. Die Bereitstellung ist gesperrt,
                bis der User den Absender bewusst auswählt (hebt die Markierung
                auf) oder sich neu anmeldet. */}
            {senderIdentityUnconfirmed && (
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#FEF3C7',
                borderRadius: '8px',
                border: '1px solid #F59E0B',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <ShieldAlert size={20} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: '#92400E',
                    margin: '0 0 4px 0',
                    fontWeight: '600'
                  }}>
                    Absender bitte bestätigen
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#B45309',
                    margin: '0 0 10px 0',
                    lineHeight: '1.4'
                  }}>
                    Der Absender konnte nicht automatisch über Ihre Anmeldung bestätigt werden — möglicherweise ist Ihre Sitzung abgelaufen.
                    Voreingestellt ist <strong>{selectedSender?.fullName || 'der primäre Mitarbeiter'}</strong>.
                    Die Bereitstellung ist gesperrt, bis Sie den Absender unten bewusst auswählen oder sich neu anmelden.
                  </p>
                  <button
                    type="button"
                    onClick={handleForceRelogin}
                    disabled={isReleasing}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#F59E0B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isReleasing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Neu anmelden
                  </button>
                </div>
              </div>
            )}
            <SenderSelection
              senders={senders}
              selectedSender={selectedSender}
              onSenderChange={handleSenderChange}
              disabled={isReleasing}
              loading={sendersLoading}
              error={sendersError}
              photoValidationErrors={photoValidationErrors}
            />
          </>
        )}

        {/* To-Do Sektion */}
        <TodoSection
          todos={filteredTodos}
          customerName={customer.company}
          onConfirmationChange={handleTodoConfirmation}
          confirmedTodos={confirmedTodos}
          showConfirmation={true}
        />

        {/* Attachment Sektion — nur wenn required Attachments existieren.
            versandmethode='duo' simuliert "kein PDF-Upload-Bereich",
            wie es UploadModal bei DUO ohne mainPdf macht. */}
        {attachments && attachments.filter(att => att.is_required).length > 0 && (
          <AttachmentSection
            attachments={attachments || []}
            customerName={customer.company}
            uploadedFiles={uploadedAttachments}
            onFileSelect={handleAttachmentSelect}
            onFileRemove={handleAttachmentRemove}
            mainPdfFile={null}
            onMainPdfSelect={() => { /* nicht genutzt im xtax_portal-Flow */ }}
            onMainPdfRemove={() => { /* nicht genutzt im xtax_portal-Flow */ }}
            versandmethode={'duo'}
            mandantennummer={customer.mandantennummer}
            month={month}
            year={year}
            abrechnungsmethode={customer.abrechnungsmethode}
          />
        )}

        {/* Custom Message Input */}
        <div style={{ marginBottom: '16px' }}>
          <CustomMessageInput
            value={customMessage}
            onChange={setCustomMessage}
            customer={customer}
            month={month}
            year={year}
            senderIdentity={selectedSender}
          />
        </div>

        {/* Versandzeitpunkt: nach der nächsten DATEV-Bereitstellung (Standard)
            oder sofort mit ausdrücklicher Bestätigung */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>
            <Clock size={16} color="#374151" />
            Versandzeitpunkt
          </label>

          {bereitstellungLoading ? (
            <div style={{
              padding: '12px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                DATEV-Bereitstellungszeiten werden geladen...
              </p>
            </div>
          ) : !bereitstellungInfo ? (
            <div style={{
              padding: '12px',
              backgroundColor: '#FEF2F2',
              borderRadius: '8px',
              border: '1px solid #FECACA',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <AlertCircle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: '1.4' }}>
                {bereitstellungFehler || 'Die DATEV-Bereitstellungszeiten konnten nicht geladen werden.'}
              </p>
            </div>
          ) : (
            <>
              {/* Option 1: nach der nächsten Bereitstellung (Standard) */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border: versandModus === 'nach_bereitstellung' ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                backgroundColor: versandModus === 'nach_bereitstellung' ? '#EFF6FF' : '#FFFFFF',
                cursor: isReleasing ? 'not-allowed' : 'pointer'
              }}>
                <input
                  type="radio"
                  name="versandModus"
                  checked={versandModus === 'nach_bereitstellung'}
                  onChange={() => handleVersandModusChange('nach_bereitstellung')}
                  disabled={isReleasing}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <p style={{ fontSize: '14px', color: '#111827', margin: '0 0 4px 0', fontWeight: '500' }}>
                    Nach der nächsten DATEV-Bereitstellung versenden (empfohlen)
                  </p>
                  <p style={{ fontSize: '13px', color: '#4B5563', margin: 0, lineHeight: '1.4' }}>
                    Nächste Bereitstellung: {formatBereitstellung(bereitstellungInfo.naechsteBereitstellung)}.
                    Versand voraussichtlich ab ca. {formatUhrzeit(bereitstellungInfo.naechsterAbruf, ANZEIGE_VERSAND_PUFFER_MINUTEN)} Uhr.
                  </p>
                </div>
              </label>

              {/* Option 2: sofort, nur mit Bestätigung */}
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px',
                borderRadius: '8px',
                border: versandModus === 'sofort' ? '2px solid #F59E0B' : '1px solid #D1D5DB',
                backgroundColor: versandModus === 'sofort' ? '#FFFBEB' : '#FFFFFF',
                cursor: isReleasing || !sofortMoeglich ? 'not-allowed' : 'pointer',
                opacity: sofortMoeglich ? 1 : 0.6
              }}>
                <input
                  type="radio"
                  name="versandModus"
                  checked={versandModus === 'sofort'}
                  onChange={() => handleVersandModusChange('sofort')}
                  disabled={isReleasing || !sofortMoeglich}
                  style={{ marginTop: '3px' }}
                />
                <div>
                  <p style={{ fontSize: '14px', color: '#111827', margin: 0, fontWeight: '500' }}>
                    Sofort versenden
                  </p>
                  {!sofortMoeglich && (
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                      Nicht verfügbar, weil die letzte DATEV-Bereitstellung nicht bekannt ist.
                    </p>
                  )}
                </div>
              </label>

              {versandModus === 'sofort' && sofortMoeglich && bereitstellungInfo.letzteBereitstellung && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '8px',
                  border: '1px solid #F59E0B'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: isReleasing ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={sofortBestaetigt}
                      onChange={(e) => {
                        setSofortBestaetigt(e.target.checked);
                        setError(null);
                      }}
                      disabled={isReleasing}
                      style={{ marginTop: '3px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#92400E', lineHeight: '1.4' }}>
                      Ich bestätige, dass alle Änderungen an dieser Abrechnung und die Übertragung an den Lohnaustauschdatenservice vor der letzten DATEV-Bereitstellung ({formatBereitstellung(bereitstellungInfo.letzteBereitstellung)}) abgeschlossen waren.
                    </span>
                  </label>
                  <p style={{ fontSize: '12px', color: '#B45309', margin: '8px 0 0 26px', lineHeight: '1.4' }}>
                    Plane mindestens 30 Minuten Puffer ein. Was danach übertragen wurde, steht erst mit der nächsten Bereitstellung zur Verfügung.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '8px',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} color="#DC2626" />
            <span style={{ fontSize: '14px', color: '#DC2626' }}>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isReleasing}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: isReleasing ? 'not-allowed' : 'pointer'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleRelease}
            disabled={isButtonDisabled}
            style={{
              padding: '8px 16px',
              backgroundColor: isButtonDisabled ? '#9CA3AF' : '#3B82F6',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            {isReleasing ? 'Wird bereitgestellt...' : 'Bereitstellen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseModal;
